import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Button, Descriptions, Drawer, Empty, Spin, Table, Tag } from "antd";
import {
  HiOutlineArrowRight,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
} from "react-icons/hi2";
import { canViewHrCommissionSystemKpi } from "../../../services/authService";
import { getHrCommissionSystemOrders } from "../../../services/fhpService";
import {
  ordersDynamicPackageDetailPath,
  ordersFlightDetailPath,
  ordersHotelDetailPath,
  ordersOrganizedTourDetailPath,
  ordersPackageDetailPath,
} from "../../../utils/ordersDashboardRoutes";
import "./HrCommissionOrdersPage.scss";

function resolveHrOrderDetailsPath(row) {
  const id = String(row?.orderId || "").trim();
  if (!id) return "";
  const source = String(row?.source || "").toLowerCase();
  if (source === "hotel") return ordersHotelDetailPath(id);
  if (source === "campaign") return ordersDynamicPackageDetailPath(id);
  const fromApi = String(row?.detailsPath || "").trim();
  if (fromApi.includes("/organized-tours/")) return ordersOrganizedTourDetailPath(id);
  if (fromApi.includes("/flights/")) return ordersFlightDetailPath(id);
  if (fromApi.includes("/packages/")) return ordersPackageDetailPath(id);
  return ordersPackageDetailPath(id);
}

/** Open order page in a new tab; attach returnTo so Back won't land on broken Orders list. */
function openOrderInNewTab(path, monthKey) {
  const href = String(path || "").trim();
  if (!href) return;
  const returnTo = `/p/dashboard/hr-commissions?month=${encodeURIComponent(monthKey || "")}`;
  const sep = href.includes("?") ? "&" : "?";
  window.open(
    `${href}${sep}returnTo=${encodeURIComponent(returnTo)}`,
    "_blank",
    "noopener,noreferrer"
  );
}

const HEBREW_MONTHS = [
  "ינואר",
  "פברואר",
  "מרץ",
  "אפריל",
  "מאי",
  "יוני",
  "יולי",
  "אוגוסט",
  "ספטמבר",
  "אוקטובר",
  "נובמבר",
  "דצמבר",
];

function currentMonthKey() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function isValidMonthKey(value) {
  return /^\d{4}-(0[1-9]|1[0-2])$/.test(String(value || ""));
}

function shiftMonthKey(monthKey, delta) {
  const [yRaw, mRaw] = String(monthKey || currentMonthKey()).split("-");
  const y = Number(yRaw);
  const m = Number(mRaw);
  if (!Number.isFinite(y) || !Number.isFinite(m)) return currentMonthKey();
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function formatMonthLabel(monthKey) {
  const [yRaw, mRaw] = String(monthKey || "").split("-");
  const y = Number(yRaw);
  const m = Number(mRaw);
  if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) {
    return monthKey || "—";
  }
  return `${HEBREW_MONTHS[m - 1]} ${y}`;
}

function formatCurrencyIls(value) {
  return new Intl.NumberFormat("he-IL", {
    style: "currency",
    currency: "ILS",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);
}

function formatDisplayDate(value) {
  if (!value) return "—";
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const [y, m, d] = s.slice(0, 10).split("-");
    return `${d}/${m}/${y}`;
  }
  return s;
}

function sourceTagColor(source) {
  if (source === "hotel") return "blue";
  if (source === "campaign") return "purple";
  return "cyan";
}

export default function HrCommissionOrdersPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const allowed = canViewHrCommissionSystemKpi();

  const monthKey = useMemo(() => {
    const fromUrl = searchParams.get("month");
    return isValidMonthKey(fromUrl) ? fromUrl : currentMonthKey();
  }, [searchParams]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    if (!allowed) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getHrCommissionSystemOrders({ month: monthKey })
      .then((res) => {
        if (cancelled) return;
        const data = res?.data || {};
        if (data.success === false || res?.status >= 400) {
          setError("לא ניתן לטעון את פירוט עמלות HR");
          setPayload(null);
          return;
        }
        setPayload(data);
      })
      .catch(() => {
        if (!cancelled) {
          setError("לא ניתן לטעון את פירוט עמלות HR");
          setPayload(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [allowed, monthKey]);

  const setMonth = (next) => {
    setSearchParams({ month: next }, { replace: true });
  };

  const orders = Array.isArray(payload?.orders) ? payload.orders : [];
  const isCurrentMonth = monthKey === currentMonthKey();
  const selectedPath = selectedOrder ? resolveHrOrderDetailsPath(selectedOrder) : "";

  const columns = useMemo(
    () => [
      {
        title: "תאריך הזמנה",
        dataIndex: "orderDate",
        key: "orderDate",
        width: 120,
        render: (v) => formatDisplayDate(v),
      },
      {
        title: "סוג",
        dataIndex: "sourceLabel",
        key: "source",
        width: 120,
        render: (label, row) => (
          <Tag color={sourceTagColor(row.source)}>{label || row.source || "—"}</Tag>
        ),
      },
      {
        title: "מלון / מוצר",
        dataIndex: "title",
        key: "title",
        ellipsis: true,
        render: (v) => v || "—",
      },
      {
        title: "אורח",
        dataIndex: "guestName",
        key: "guestName",
        width: 140,
        ellipsis: true,
        render: (v) => v || "—",
      },
      {
        title: "מזהה הזמנה",
        dataIndex: "orderId",
        key: "orderId",
        width: 120,
        render: (v, row) => {
          const ref = row.bookingRef || v || "—";
          if (!v) return ref;
          return (
            <button
              type="button"
              className="hr-commissions-page__order-link"
              onClick={() => setSelectedOrder(row)}
            >
              {ref}
            </button>
          );
        },
      },
      {
        title: "סוכן",
        dataIndex: "agentName",
        key: "agentName",
        width: 150,
        ellipsis: true,
        render: (name, row) => name || row.agentId || "—",
      },
      {
        title: "עמלת HR",
        dataIndex: "hrIls",
        key: "hrIls",
        width: 130,
        align: "left",
        render: (v) => (
          <span className="hr-commissions-page__hr-amount">
            {formatCurrencyIls(v)}
          </span>
        ),
      },
    ],
    []
  );

  if (!allowed) {
    return <Navigate to="/p/dashboard/home" replace />;
  }

  return (
    <div className="hr-commissions-page" dir="rtl">
      <header className="hr-commissions-page__header">
        <div className="hr-commissions-page__header-main">
          <Button
            type="text"
            className="hr-commissions-page__back"
            icon={<HiOutlineArrowRight />}
            onClick={() => navigate("/p/dashboard/home")}
          >
            חזרה לדף הבית
          </Button>
          <div>
            <h1 className="hr-commissions-page__title">פירוט עמלות HR</h1>
            <p className="hr-commissions-page__subtitle">
              הזמנות לפי תאריך יצירה/תשלום בחודש הנבחר
            </p>
          </div>
        </div>

        <div className="hr-commissions-page__month-nav" aria-label="בחירת חודש">
          <button
            type="button"
            className="hr-commissions-page__month-btn"
            onClick={() => setMonth(shiftMonthKey(monthKey, 1))}
            disabled={isCurrentMonth || loading}
            aria-label="חודש הבא"
          >
            <HiOutlineChevronRight />
          </button>
          <span className="hr-commissions-page__month-label">
            {formatMonthLabel(monthKey)}
          </span>
          <button
            type="button"
            className="hr-commissions-page__month-btn"
            onClick={() => setMonth(shiftMonthKey(monthKey, -1))}
            disabled={loading}
            aria-label="חודש קודם"
          >
            <HiOutlineChevronLeft />
          </button>
        </div>
      </header>

      <section className="hr-commissions-page__summary" aria-label="סיכום חודש">
        <div className="hr-commissions-page__summary-item">
          <span className="hr-commissions-page__summary-label">סה״כ עמלות HR</span>
          <strong className="hr-commissions-page__summary-value">
            {loading ? "—" : formatCurrencyIls(payload?.monthIls)}
          </strong>
        </div>
        <div className="hr-commissions-page__summary-item">
          <span className="hr-commissions-page__summary-label">הזמנות</span>
          <strong className="hr-commissions-page__summary-value">
            {loading
              ? "—"
              : new Intl.NumberFormat("he-IL").format(payload?.orderCount || 0)}
          </strong>
        </div>
        <div className="hr-commissions-page__summary-item">
          <span className="hr-commissions-page__summary-label">מלונות</span>
          <strong className="hr-commissions-page__summary-value">
            {loading ? "—" : formatCurrencyIls(payload?.hotelsIls)}
          </strong>
        </div>
        <div className="hr-commissions-page__summary-item">
          <span className="hr-commissions-page__summary-label">חבילות</span>
          <strong className="hr-commissions-page__summary-value">
            {loading ? "—" : formatCurrencyIls(payload?.ldsIls)}
          </strong>
        </div>
        <div className="hr-commissions-page__summary-item">
          <span className="hr-commissions-page__summary-label">קמפיין</span>
          <strong className="hr-commissions-page__summary-value">
            {loading ? "—" : formatCurrencyIls(payload?.campaignIls)}
          </strong>
        </div>
        <div
          className="hr-commissions-page__summary-item hr-commissions-page__summary-item--baggage"
          title="BAGGAGE_FEE_FLAT בלבד (לא כולל עלות ספק) — לא נכלל בסה״כ עמלות HR"
        >
          <span className="hr-commissions-page__summary-label">
            מזוודות / טרולי
          </span>
          <strong className="hr-commissions-page__summary-value">
            {loading ? "—" : formatCurrencyIls(payload?.baggageMarkupIls)}
          </strong>
          {!loading && Number(payload?.baggageOrderCount) > 0 ? (
            <span className="hr-commissions-page__summary-hint">
              BAGGAGE_FEE_FLAT · {payload.baggageOrderCount} הזמנות
            </span>
          ) : null}
        </div>
      </section>

      <section className="hr-commissions-page__table-wrap">
        {loading ? (
          <div className="hr-commissions-page__loading">
            <Spin size="large" />
          </div>
        ) : error ? (
          <Empty description={error} />
        ) : (
          <Table
            rowKey={(row) =>
              `${row.source || "x"}-${row.orderId || "0"}-${row.orderDate || ""}`
            }
            columns={columns}
            dataSource={orders}
            onRow={(row) => ({
              onClick: () => setSelectedOrder(row),
              style: { cursor: "pointer" },
            })}
            pagination={{
              pageSize: 50,
              showSizeChanger: true,
              pageSizeOptions: ["25", "50", "100"],
              showTotal: (total) => `${total} הזמנות`,
            }}
            locale={{ emptyText: <Empty description="אין הזמנות עם עמלת HR בחודש זה" /> }}
            scroll={{ x: 960 }}
            size="middle"
          />
        )}
      </section>

      <Drawer
        title="פרטי עמלת HR"
        placement="left"
        width={420}
        open={Boolean(selectedOrder)}
        onClose={() => setSelectedOrder(null)}
        destroyOnClose
        extra={
          selectedPath ? (
            <Button
              type="primary"
              onClick={() => openOrderInNewTab(selectedPath, monthKey)}
            >
              פתח הזמנה מלאה
            </Button>
          ) : null
        }
      >
        {selectedOrder ? (
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="סוג">
              <Tag color={sourceTagColor(selectedOrder.source)}>
                {selectedOrder.sourceLabel || selectedOrder.source || "—"}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="מלון / מוצר">
              {selectedOrder.title || "—"}
            </Descriptions.Item>
            <Descriptions.Item label="אורח">
              {selectedOrder.guestName || "—"}
            </Descriptions.Item>
            <Descriptions.Item label="סוכן">
              {selectedOrder.agentName || selectedOrder.agentId || "—"}
            </Descriptions.Item>
            <Descriptions.Item label="מזהה הזמנה">
              {selectedOrder.bookingRef || selectedOrder.orderId || "—"}
            </Descriptions.Item>
            <Descriptions.Item label="תאריך הזמנה">
              {formatDisplayDate(selectedOrder.orderDate)}
            </Descriptions.Item>
            <Descriptions.Item label="צ׳ק־אין">
              {formatDisplayDate(selectedOrder.checkIn)}
            </Descriptions.Item>
            <Descriptions.Item label="צ׳ק־אאוט">
              {formatDisplayDate(selectedOrder.checkOut)}
            </Descriptions.Item>
            <Descriptions.Item label="עמלת HR">
              <strong className="hr-commissions-page__hr-amount">
                {formatCurrencyIls(selectedOrder.hrIls)}
              </strong>
            </Descriptions.Item>
          </Descriptions>
        ) : null}
      </Drawer>
    </div>
  );
}
